import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Clock, Quote, Sparkles } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import { Footer } from "../home/sections/homepage/footer";
import { blogPosts } from "./data";

export const BlogArticlePage = () => {
  const { slug } = useParams();
  const post = blogPosts.find((entry) => entry.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b ${post.theme.gradient}`}>
      <section className="relative pt-28 pb-12 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 left-0 w-80 h-80 bg-sky-200/50 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200/50 blur-3xl" />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to blog
          </Link>

          <div className="mt-6 grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-3 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur border border-white/70 text-sm font-medium text-gray-700">
                <Sparkles className="w-4 h-4 text-sky-600" />
                {post.tags.join(" / ")}
              </div>
              <h1 className="font-noto-serif text-4xl md:text-5xl font-semibold text-gray-900 leading-tight">
                {post.title}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">{post.description}</p>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm">
                  <BookOpen className="w-4 h-4 text-emerald-600" />
                  {post.readTime}
                </span>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gray-200 shadow-sm">
                  <Clock className="w-4 h-4 text-sky-600" />
                  {post.publishedOn}
                </span>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="bg-gray-900 text-white rounded-2xl p-6 shadow-xl border border-gray-800">
                <p className="text-sm uppercase tracking-[0.15em] text-gray-400">Why this matters</p>
                <p className="mt-3 text-lg leading-relaxed text-white/90">
                  Hiring teams now read thousands of AI-written resumes. The signal that stands out
                  is how clearly a person can show their thinking, communication, and depth.
                </p>
                {post.pullQuote ? (
                  <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10">
                    <div className="flex items-start gap-3">
                      <Quote className="w-5 h-5 text-emerald-300" />
                      <p className="font-noto-serif italic text-base leading-relaxed text-white/90">{post.pullQuote}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-10 space-y-10">
            {post.sections.map((section, idx) => (
              <article key={section.heading ?? idx} className="space-y-4">
                {section.heading ? (
                  <motion.h2
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="font-noto-serif text-2xl md:text-3xl font-semibold text-gray-900"
                  >
                    {section.heading}
                  </motion.h2>
                ) : null}
                {section.body.map((paragraph, pIdx) => (
                  <motion.p
                    key={pIdx}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-lg text-gray-700 leading-relaxed"
                  >
                    {paragraph}
                  </motion.p>
                ))}
                {section.bullets ? (
                  <ul className="mt-4 space-y-3">
                    {section.bullets.map((bullet) => (
                      <li
                        key={bullet}
                        className="flex items-start gap-3 text-lg text-gray-700 leading-relaxed"
                      >
                        <span className="mt-2 h-2 w-2 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>

          <div className="mt-10 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm uppercase tracking-[0.12em] text-gray-500">Next up</p>
              <p className="text-base text-gray-700">
                More signals, less noise. The next stories dive deeper into how we measure real skill.
              </p>
            </div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 hover:text-sky-900"
            >
              Browse all posts
              <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
